import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDischargeSummary } from "../../../store/slices/dischargeSummarySlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function DischargeSummaryFormPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDischargeSummary, isLoading, error } = useSelector(
    (state) => state.dischargeSummary || {}
  );

  useEffect(() => {
    if (id) dispatch(fetchDischargeSummary(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDischargeSummary) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentDischargeSummary, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentDischargeSummary) return <div className="p-8 text-center">No record found.</div>;

  const d = currentDischargeSummary;

  const hasType = (type) =>
    d.discharge_types?.includes(type);

  const fmtDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  // Helper to parse comma-separated string into array
  const parseCheckboxArray = (str) => {
    if (!str) return [];
    if (Array.isArray(str)) return str;
    return str.split(',').map(item => item.trim()).filter(item => item);
  };

  // Helper to get image URL for display
  const getImageUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith("http")) return filePath;
    return `http://127.0.0.1:8000/storage/${filePath}`;
  };

  // Helper to check if file is an image
  const isImageFile = (filePath) => {
    if (!filePath) return false;
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = filePath.toLowerCase().substring(filePath.lastIndexOf('.'));
    return imageExts.includes(ext);
  };

  // Helper to render text as bullet list if it contains newlines or bullets
  const renderBulletList = (text) => {
    if (!text) return null;
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length > 1 || lines.some(line => /^[•\*\-\u2022]/.test(line.trim()))) {
      return (
        <ul className="bullet-list">
          {lines.map((line, idx) => {
            let cleanLine = line.trim().replace(/^[•\*\-\u2022]+\s*/, '');
            if (!cleanLine) return null;
            return <li key={idx}>{cleanLine}</li>;
          })}
        </ul>
      );
    }
    return <div style={{ textAlign: "left" }}>{text}</div>;
  };

  return (
    <>
      <style jsx global>{`
        * {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }

        body {
          background: #fff !important;
          color: #000 !important;
          font-size: 11px !important;
        }

        .print-container {
          padding: 30px !important;
          position: relative !important;
        }

        .logo {
          position: absolute !important;
          top: 15px !important;
          left: 15px !important;
        }

        .logo img {
          height: 60px !important;
        }

        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          border-bottom: 2px solid #000 !important;
          padding-bottom: 10px !important;
        }

        .header h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
        }

        .header h6 {
          font-size: 1rem !important;
          text-decoration: underline !important;
        }

        .section-title {
          background: #eee !important;
          padding: 4px 8px !important;
          font-weight: bold !important;
          margin: 15px 0 8px 0 !important;
          border: 1px solid #000 !important;
          text-transform: uppercase !important;
        }

        .info-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 10px !important;
        }

        .info-table td,
        .info-table th {
          border: 1px solid #000 !important;
          padding: 6px !important;
          vertical-align: top !important;
        }

        .label {
          font-weight: bold !important;
          width: 25% !important;
          background: #f9f9f9 !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 8px !important;
          min-height: 60px !important;
          text-align: left !important;
        }

        .bullet-list {
          padding-left: 20px !important;
          margin: 0 !important;
          list-style-position: inside !important;
          text-align: left !important;
        }
        
        .bullet-list li {
          margin-bottom: 4px !important;
          text-align: left !important;
          display: list-item !important;
        }

        .med-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 5px !important;
        }

        .med-table th,
        .med-table td {
          border: 1px solid #000 !important;
          padding: 6px !important;
          text-align: center !important;
        }

        .med-table th {
          background: #f2f2f2 !important;
          font-weight: bold !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1px solid #000 !important;
          padding-top: 8px !important;
          text-align: center !important;
          font-size: 10px !important;
        }

        .checkbox-grid {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          gap: 6px !important;
        }

        .checkbox-item {
          display: flex !important;
          align-items: flex-start !important;
          gap: 6px !important;
        }

        .checkbox-item input[type="checkbox"] {
          margin-top: 2px !important;
        }

        @media print {
          .page-break {
            page-break-before: always !important;
          }
        }
      `}</style>


      <div className="print-container">
        <div className="logo">
          <img src={logo} alt="Logo" />
        </div>

        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>DISCHARGE SUMMARY REPORT</h6>
        </div>

        {/* CLIENT INFO */}
        <div className="section-title">Client Information</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Name</td>
              <td>{d.client_name}</td>
              <td className="label">CNIC</td>
              <td>{d.CNIC}</td>
            </tr>
            <tr>
              <td className="label">Gender</td>
              <td>{d.gender}</td>
              <td className="label">Program</td>
              <td>{d.program}</td>
            </tr>
            <tr>
              <td className="label">Date of Admission</td>
              <td>{fmtDate(d.admission_date)}</td>
              <td className="label">Date of Discharge</td>
              <td>{fmtDate(d.discharge_date)}</td>
            </tr>
            <tr>
              <td className="label">Reason For Discharge</td>
              <td colSpan="3">{d.discharge_reason}</td>
            </tr>
          </tbody>
        </table>

        {/* DIAGNOSIS */}
        <div className="section-title">Provisional Psychiatric Diagnosis</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Primary Diagnosis</td>
              <td colSpan="3">{d.primary_diagnosis}</td>
            </tr>
            <tr>
              <td className="label">Secondary / Co-morbidities</td>
              <td colSpan="3">{d.secondary_diagnosis}</td>
            </tr>
          </tbody>
        </table>

        {/* DISCHARGE PLANNING */}
        <div className="section-title">Discharge Planning</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Discharge Type</td>
              <td colSpan="3">
                {parseCheckboxArray(d.discharge_types)?.length > 0 ? (
                  parseCheckboxArray(d.discharge_types).map((type, idx) => (
                    <div key={idx}>
                      {type === "Other" && d.discharge_type_other_value
                        ? `${type} (${d.discharge_type_other_value})`
                        : type}
                    </div>
                  ))
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Additional Discharge Options</td>
              <td colSpan="3">
                {parseCheckboxArray(d.discharge_extras)?.length > 0 ? (
                  parseCheckboxArray(d.discharge_extras).map((extra, idx) => (
                    <div key={idx}>{extra}</div>
                  ))
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Teaching Provided To</td>
              <td colSpan="3">
                {parseCheckboxArray(d.teaching_provided_to)?.length > 0 ? (
                  parseCheckboxArray(d.teaching_provided_to).map((teaching, idx) => (
                    <div key={idx}>{teaching}</div>
                  ))
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Discharge Note</td>
              <td colSpan="3">{d.discharge_note}</td>
            </tr>
            <tr>
              <td className="label">Followup Plan</td>
              <td colSpan="3">{d.follow_up_plan}</td>
            </tr>
            <tr>
              <td className="label">Special Instructions</td>
              <td colSpan="3">{d.special_instructions}</td>
            </tr>
            <tr>
              <td className="label">Injections</td>
              <td colSpan="3">{d.injections}</td>
            </tr>
            <tr>
              <td className="label">Red Flags</td>
              <td colSpan="3">{d.red_flags}</td>
            </tr>
            <tr>
              <td className="label">Risk Assessment</td>
              <td colSpan="3">{d.risk_assessment}</td>
            </tr>
          </tbody>
        </table>

        {/* PRESENTING SYMPTOMS */}
        <div className="section-title">Presenting Symptoms</div>
        <div className="text-box">{d.presenting_symptoms}</div>

        {/* SERVICES PROVIDED */}
        <div className="section-title">Services Provided at TRH</div>
        <div className="text-box">
          <div className="checkbox-grid">
            {[
              "Psychiatric consultation & treatment",
              "Primary-care services",
              "Psychiatric rehabilitation services",
              "Individual Therapy",
              "Group Therapy",
              "Psychoeducation",
              "Family Counseling",
              "Recreational / Occupational Therapy"
            ].map((option, idx) => {
              const checked = parseCheckboxArray(d.service_provided).includes(option);
              return (
                <div key={idx} className="checkbox-item">
                  <input type="checkbox" checked={checked} readOnly />
                  <label>{option}</label>
                </div>
              );
            })}
          </div>
        </div>

        {/* CLINICAL REPORTS */}
        <div className="section-title">Clinical Reports</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Primary Care Remarks</td>
              <td colSpan="3">{d.primary_care_remarks}</td>
            </tr>
            <tr>
              <td className="label">Primary Care Attachment</td>
              <td colSpan="3">
                {d.primary_care_attachment ? (
                  isImageFile(d.primary_care_attachment) ? (
                    <img src={getImageUrl(d.primary_care_attachment)} alt="Primary Care" style={{ maxHeight: '150px', maxWidth: '100%' }} />
                  ) : (
                    <a href={getImageUrl(d.primary_care_attachment)} target="_blank" rel="noopener noreferrer">View Document</a>
                  )
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Psychiatrist Remarks</td>
              <td colSpan="3">{d.psychiatrist_remarks}</td>
            </tr>
            <tr>
              <td className="label">Psychiatrist Attachment</td>
              <td colSpan="3">
                {d.psychiatrist_attachment ? (
                  isImageFile(d.psychiatrist_attachment) ? (
                    <img src={getImageUrl(d.psychiatrist_attachment)} alt="Psychiatrist Attachment" style={{ maxHeight: '150px', maxWidth: '100%' }} />
                  ) : (
                    <a href={getImageUrl(d.psychiatrist_attachment)} target="_blank" rel="noopener noreferrer">View Document</a>
                  )
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Care Manager Remarks</td>
              <td colSpan="3">{d.care_manager_remarks}</td>
            </tr>
            <tr>
              <td className="label">Care Manager Attachment</td>
              <td colSpan="3">
                {d.care_manager_attachment ? (
                  isImageFile(d.care_manager_attachment) ? (
                    <img src={getImageUrl(d.care_manager_attachment)} alt="Care Manager Attachment" style={{ maxHeight: '150px', maxWidth: '100%' }} />
                  ) : (
                    <a href={getImageUrl(d.care_manager_attachment)} target="_blank" rel="noopener noreferrer">View Document</a>
                  )
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
          </tbody>
        </table>

        {/* CLINICAL PROGRESS */}
        <div className="section-title">Clinical Progress & Mental State</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Clinical Progress</td>
              <td>
                {parseCheckboxArray(d.clinical_progress)?.length > 0 ? (
                  parseCheckboxArray(d.clinical_progress).map((item, idx) => (
                    <div key={idx}>{item}</div>
                  ))
                ) : (
                  d.clinical_progress || "N/A"
                )}
              </td>
              <td className="label">Mental State</td>
              <td>
                {parseCheckboxArray(d.mental_state_at_discharge)?.length > 0 ? (
                  parseCheckboxArray(d.mental_state_at_discharge).map((item, idx) => (
                    <div key={idx}>{item}</div>
                  ))
                ) : (
                  d.mental_state_at_discharge || "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Complications</td>
              <td colSpan="3">{d.complications || "N/A"}</td>
            </tr>
          </tbody>
        </table>

        {/* MEDICATIONS */}
        <div className="section-title">Discharge Medications</div>
        <table className="med-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Dosage</th>
              <th>Frequency</th>
              <th>Route</th>
            </tr>
          </thead>
          <tbody>
            {d.medications.map((m, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{m.name}</td>
                <td>{m.dosage}</td>
                <td>{m.frequency}</td>
                <td>{m.route}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* INSTRUCTIONS */}
        <div className="section-title">Instructions</div>
        <div className="text-box">
          <div className="checkbox-grid">
            {[
              "Verbal instructions given",
              "Written prescription attached",
              "Continue till next psychiatric review",
              "Side effects & compliance discussed"
            ].map((option, idx) => {
              const checked = parseCheckboxArray(d.instructions).includes(option);
              return (
                <div key={idx} className="checkbox-item">
                  <input type="checkbox" checked={checked} readOnly />
                  <label>{option}</label>
                </div>
              );
            })}
          </div>
        </div>

        {/* FAMILY COUNSELING */}
        <div className="section-title">Family Counseling</div>
        <div className="text-box">
          <div className="checkbox-grid">
            {[
              "Educated about illness condition",
              "Educated on hygiene care at home",
              "Educated on medication use & side effects",
              "Medicine to be kept with family & supervised",
              "Monitor unusual behaviors & report in OPD",
              "After giving medicine -> check patient’s mouth -> supervise for 20 mins",
              "Informed about follow-up after 14 days / earlier if symptoms worsen",
              "Educated on adjusting family expectations"
            ].map((option, idx) => {
              const checked = parseCheckboxArray(d.family_counseling).includes(option);
              return (
                <div key={idx} className="checkbox-item">
                  <input type="checkbox" checked={checked} readOnly />
                  <label>{option}</label>
                </div>
              );
            })}
          </div>
        </div>

        {/* HOME CARE */}
        <div className="section-title">Home Care & Future Support</div>
        <table className="info-table">
          <tbody>
            <tr><td className="label">Possible Relapse Signs</td><td colSpan="3">{d.possible_relapse_signs}</td></tr>
            <tr><td className="label">Recovery Goals</td><td colSpan="3">{d.recovery_goals}</td></tr>
            <tr><td className="label">Who to Contact</td><td colSpan="3">{d.who_to_contact}</td></tr>
            <tr><td className="label">Where to Go in Crisis</td><td colSpan="3">{d.where_to_go_crisis}</td></tr>
            <tr><td className="label">Medication Details</td><td colSpan="3">{d.medication_details}</td></tr>
            <tr><td className="label">Treatment Plan</td><td colSpan="3">{d.treatment_plan}</td></tr>
            <tr><td className="label">Physical Health Needs</td><td colSpan="3">{d.physical_health_needs}</td></tr>
            <tr><td className="label">Date of Review</td><td colSpan="3">{fmtDate(d.date_of_review)}</td></tr>
            <tr><td className="label">Admission Pathway</td><td colSpan="3">{d.admission_pathway}</td></tr>
          </tbody>
        </table>

        {/* FOLLOW UP */}
        <div className="section-title">Follow-up & Referral</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Follow-up Psychiatrist</td>
              <td>{fmtDate(d.follow_up_psychiatrist)}</td>
              <td className="label">Referred For</td>
              <td>
                {parseCheckboxArray(d.referred_for)?.length > 0 ? (
                  parseCheckboxArray(d.referred_for).map((item, idx) => (
                    <div key={idx}>
                      {item === "Other" && d.referred_for_other_text
                        ? `${item} (${d.referred_for_other_text})`
                        : item}
                    </div>
                  ))
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
            <tr>
              <td className="label">Next of Kin Informed</td>
              <td>{d.next_of_kin_informed}</td>
              <td className="label">Emergency Contact Provided</td>
              <td>{d.emergency_contact_provided}</td>
            </tr>
            <tr>
              <td className="label">Safety Care Plan</td>
              <td colSpan="3">{d.safety_care_plan}</td>
            </tr>
          </tbody>
        </table>

        {/* TRH RECOMMENDATIONS */}
        <div className="section-title">TRH Recommendations</div>
        <div className="text-box">{renderBulletList(d.trh_recommendations)}</div>

        <div className="section-title">Signatures</div>
        <table className="info-table">
          <tbody>
            <tr>
              <td className="label">Psychiatric Consultant</td>
              <td></td>
              <td className="label">Senior Psychiatric Rehabilitation Practitioner, TRH</td>
              <td></td>
            </tr>
            <tr>
              <td className="label">Nursing Manager, TRH</td>
              <td></td>
              <td className="label">Medical Director, TRH</td>
              <td></td>
            </tr>
            <tr>
              <td className="label">Rehab Navigator</td>
              <td></td>
              <td className="label">Guardian/Client</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
